import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import type { NavKey } from '@/brand/types';
import type { ScreenId } from './types';

// Единый реестр экранов защищённой зоны. Раньше карта путей была
// продублирована трижды — в router.tsx, в MENU_ITEMS и в usePageTitle;
// расхождения ловились только глазами. Теперь источник один, а бренд
// перечисляет, какие экраны у него включены (src/brand/*).
//
// Чтобы спрятать экран у одного бренда, его не нужно удалять из реестра —
// достаточно не включать в brand.screens. Но экран, скрытый у ВСЕХ брендов,
// из реестра убирается: пока его lazy-импорт в графе, Rollup собирает чанк,
// который никогда не грузится. Так поступили с CPC (см. ниже).

export type ScreenDef = {
  id: ScreenId;
  // Абсолютный путь. Роуты живут под pathless layout-роутом, поэтому
  // абсолютные пути у детей допустимы и читаются лучше относительных.
  path: string;
  // Ключ заголовка в словаре (namespace `nav`). Он же — подпись пункта меню по
  // умолчанию; бренд переопределяет ключ в своём словаре («Мои ссылки» →
  // «Мои потоки»), а подпись, которая в меню короче заголовка («Основная»
  // против «Статистики»), задаётся в nav отдельным ключом.
  titleKey: NavKey;
  element: LazyExoticComponent<ComponentType>;
};

// Named-экспорты страниц маппим на default, как требует React.lazy.
const Dashboard = lazy(() => import('@/pages/dashboard/Dashboard').then((m) => ({ default: m.Dashboard })));
const Settings = lazy(() => import('@/pages/settings/Settings').then((m) => ({ default: m.Settings })));
const TwoFactor = lazy(() => import('@/pages/settings/TwoFactor').then((m) => ({ default: m.TwoFactor })));
const Streams = lazy(() => import('@/pages/streams/Streams').then((m) => ({ default: m.Streams })));
const CreateStream = lazy(() => import('@/pages/streams/CreateStream').then((m) => ({ default: m.CreateStream })));
const EditStream = lazy(() => import('@/pages/streams/EditStream').then((m) => ({ default: m.EditStream })));
const IpRates = lazy(() => import('@/pages/streams/IpRates').then((m) => ({ default: m.IpRates })));
const GlobalLinksPage = lazy(() =>
  import('@/pages/streams/globalLinks/GlobalLinksPage').then((m) => ({ default: m.GlobalLinksPage })),
);
const PwaRedirect = lazy(() => import('@/pages/pwa/PwaRedirect').then((m) => ({ default: m.PwaRedirect })));
const Landings = lazy(() => import('@/pages/landings/Landings').then((m) => ({ default: m.Landings })));
const Sources = lazy(() => import('@/pages/sources/Sources').then((m) => ({ default: m.Sources })));
const News = lazy(() => import('@/pages/news/News').then((m) => ({ default: m.News })));
const Faq = lazy(() => import('@/pages/faq/Faq').then((m) => ({ default: m.Faq })));
const Payments = lazy(() => import('@/pages/payments/Payments').then((m) => ({ default: m.Payments })));
const StatisticsReferals = lazy(() =>
  import('@/pages/referals/StatisticsReferals').then((m) => ({ default: m.StatisticsReferals })),
);
const StatisticsGeneral = lazy(() =>
  import('@/pages/stat/StatisticsGeneral').then((m) => ({ default: m.StatisticsGeneral })),
);
const StatisticsSubscriptions = lazy(() =>
  import('@/pages/stat/StatisticsSubscriptions').then((m) => ({ default: m.StatisticsSubscriptions })),
);
const StatisticsTransactions = lazy(() =>
  import('@/pages/stat/StatisticsTransactions').then((m) => ({ default: m.StatisticsTransactions })),
);
const StatisticsTrafficBack = lazy(() =>
  import('@/pages/stat/StatisticsTrafficBack').then((m) => ({ default: m.StatisticsTrafficBack })),
);
const TDS = lazy(() => import('@/pages/tds/TDS').then((m) => ({ default: m.TDS })));
const Support = lazy(() => import('@/pages/support/Support').then((m) => ({ default: m.Support })));
const Ticket = lazy(() => import('@/pages/support/Ticket').then((m) => ({ default: m.Ticket })));
const Rules = lazy(() => import('@/pages/rules/Rules').then((m) => ({ default: m.Rules })));

export const SCREENS: ScreenDef[] = [
  // Обзорный экран: включён только у serious — у wapclick для него нет макета
  // (решение 16.09.2026), и он же служит стартовым экраном бренда (homePath).
  { id: 'dashboard', path: '/dashboard', titleKey: 'dashboard', element: Dashboard },
  { id: 'stat.general', path: '/stat', titleKey: 'stat', element: StatisticsGeneral },
  { id: 'stat.trafficback', path: '/stat/tb', titleKey: 'trafficback', element: StatisticsTrafficBack },
  // CPC скрыт решением 2026-06-10 и намеренно отсутствует в реестре, иначе
  // его чанк уезжает в дистрибутив мёртвым грузом. Страница цела —
  // src/pages/stat/StatisticsCPC.tsx. Вернуть: id в SCREEN_IDS, запись сюда
  // ({ id: 'stat.cpc', path: '/stat/cpc', titleKey: … , element: … }), ключ в
  // словаре nav и экран в screens нужного бренда.
  { id: 'subscriptions', path: '/subscription', titleKey: 'subscriptions', element: StatisticsSubscriptions },
  {
    id: 'transactions',
    path: '/subscription/transactions',
    titleKey: 'transactions',
    element: StatisticsTransactions,
  },
  { id: 'streams', path: '/streams', titleKey: 'streamsList', element: Streams },
  { id: 'streams.new', path: '/streams/new', titleKey: 'streamsCreate', element: CreateStream },
  { id: 'streams.edit', path: '/streams/:id/edit', titleKey: 'streamsEdit', element: EditStream },
  { id: 'iprates', path: '/iprates', titleKey: 'iprates', element: IpRates },
  // Глобальные постбек и трафбэк вкладкой группы «Потоки» — только у бренда с
  // layout.globalLinks = 'tab' (wapclick); у остальных те же поля в модалке или
  // в настройках, без отдельного роута.
  { id: 'globalLinks', path: '/streams/global-links', titleKey: 'globalLinks', element: GlobalLinksPage },
  // Экран без содержимого: просит тикет и уводит партнёра во внешний PWA-сервис
  // (src/pages/pwa/PwaRedirect.tsx). В реестре он потому, что пункт меню в нашей
  // модели — это всегда экран. Включён только у serious: сервис живёт на его
  // поддомене, а на инстансе без HANDOFF_URL ручка отвечает 503.
  { id: 'pwa', path: '/pwa', titleKey: 'pwa', element: PwaRedirect },
  { id: 'sources', path: '/sources', titleKey: 'sources', element: Sources },
  { id: 'landings', path: '/landings', titleKey: 'landings', element: Landings },
  { id: 'tds', path: '/tds', titleKey: 'tds', element: TDS },
  { id: 'payments', path: '/payments', titleKey: 'payments', element: Payments },
  { id: 'referals', path: '/referals', titleKey: 'referals', element: StatisticsReferals },
  { id: 'news', path: '/news', titleKey: 'news', element: News },
  { id: 'faq', path: '/faq', titleKey: 'faq', element: Faq },
  { id: 'rules', path: '/rules', titleKey: 'rules', element: Rules },
  { id: 'support', path: '/support', titleKey: 'support', element: Support },
  { id: 'support.ticket', path: '/support/:id', titleKey: 'ticket', element: Ticket },
  { id: 'settings', path: '/settings', titleKey: 'settings', element: Settings },
  { id: 'settings.2fa', path: '/settings/2fa', titleKey: 'twoFactor', element: TwoFactor },
];

const byId = new Map(SCREENS.map((screen) => [screen.id, screen]));

export function getScreen(id: ScreenId): ScreenDef {
  const screen = byId.get(id);
  // Недостижимо при валидном конфиге (SCREEN_IDS и реестр сверяются тестом),
  // но молчаливый undefined в роутере отладить куда дороже.
  if (!screen) throw new Error(`[navigation] Экран "${id}" отсутствует в реестре`);
  return screen;
}
